import React, { useEffect, useState } from 'react';
import { checklistAPI } from '../services/api';
import { ChecklistCategory, ChecklistQuestion } from '../types';

// ── Reusable tag/pill picker ─────────────────────────────────────────────────
interface TagPickerProps {
  label: string;
  allTags: string[];
  selected: string[];        // [] = applies to all
  onChange: (next: string[]) => void;
  colorActive?: string;
  colorAll?: string;
}
const TagPicker: React.FC<TagPickerProps> = ({
  label, allTags, selected, onChange,
  colorActive = 'bg-blue-600 text-white border-blue-600',
  colorAll = 'bg-blue-600 text-white',
}) => {
  const isAll = selected.length === 0;
  const toggle = (tag: string) => {
    onChange(selected.includes(tag) ? selected.filter((t) => t !== tag) : [...selected, tag]);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs text-gray-500">{label}</label>
        <button type="button" onClick={() => onChange([])}
          className={`text-xs px-1.5 py-0.5 rounded transition-colors ${isAll ? colorAll : 'text-blue-600 hover:bg-blue-50 border border-blue-200'}`}>
          All
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5 p-2 border border-gray-200 rounded-lg bg-gray-50">
        {allTags.map((tag) => (
          <button key={tag} type="button" onClick={() => toggle(tag)}
            className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
              isAll || selected.includes(tag)
                ? colorActive
                : 'bg-white text-gray-500 border-gray-300 hover:border-blue-400'
            }`}>
            {tag}
          </button>
        ))}
      </div>
      {!isAll && (
        <p className="text-xs text-gray-400 mt-1">{selected.length} selected — others excluded.</p>
      )}
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────
const AdminPage: React.FC = () => {
  const [allIndustries, setAllIndustries] = useState<string[]>([]);
  const [workforceTypeOpts, setWorkforceTypeOpts] = useState<string[]>([]);
  const [workModeOpts, setWorkModeOpts] = useState<string[]>([]);
  const [orgSizeBandOpts, setOrgSizeBandOpts] = useState<string[]>([]);
  const [categories, setCategories] = useState<ChecklistCategory[]>([]);
  const [selectedCat, setSelectedCat] = useState<ChecklistCategory | null>(null);
  const [questions, setQuestions] = useState<ChecklistQuestion[]>([]);
  const [showNewQ, setShowNewQ] = useState(false);
  const [newQ, setNewQ] = useState({
    question_text: '', requirement_type: 'must_have', question_type: 'yes_no',
    max_score: 1, evidence_required: false, guidance_note: '',
    industries: [] as string[], workforce_types: [] as string[],
    work_modes: [] as string[], org_size_bands: [] as string[],
  });
  const [editingQ, setEditingQ] = useState<number | null>(null);
  const [editIndustries, setEditIndustries] = useState<string[]>([]);
  const [editWorkforceTypes, setEditWorkforceTypes] = useState<string[]>([]);
  const [editWorkModes, setEditWorkModes] = useState<string[]>([]);
  const [editOrgSizeBands, setEditOrgSizeBands] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      checklistAPI.taxonomy(),
      checklistAPI.categories(),
    ]).then(([taxRes, catRes]) => {
      setAllIndustries(Object.keys(taxRes.data.industry_taxonomy));
      setWorkforceTypeOpts(taxRes.data.workforce_types);
      setWorkModeOpts(taxRes.data.work_modes);
      setOrgSizeBandOpts(taxRes.data.org_size_bands);
      setCategories(catRes.data);
      if (catRes.data.length > 0) loadQuestions(catRes.data[0]);
      setLoading(false);
    });
  }, []);

  const loadQuestions = async (cat: ChecklistCategory) => {
    setSelectedCat(cat);
    const res = await checklistAPI.questions({ category_id: cat.id });
    setQuestions(res.data);
  };

  const handleCreateQuestion = async () => {
    if (!selectedCat || !newQ.question_text.trim()) return;
    setSaving(true);
    await checklistAPI.createQuestion({
      ...newQ,
      category_id: selectedCat.id,
      order_index: questions.length,
      industries: newQ.industries.length > 0 ? newQ.industries : null,
      workforce_types: newQ.workforce_types.length > 0 ? newQ.workforce_types : null,
      work_modes: newQ.work_modes.length > 0 ? newQ.work_modes : null,
      org_size_bands: newQ.org_size_bands.length > 0 ? newQ.org_size_bands : null,
    });
    setShowNewQ(false);
    setNewQ({
      question_text: '', requirement_type: 'must_have', question_type: 'yes_no',
      max_score: 1, evidence_required: false, guidance_note: '',
      industries: [], workforce_types: [], work_modes: [], org_size_bands: [],
    });
    await loadQuestions(selectedCat);
    setSaving(false);
  };

  const openIndustryEdit = (q: ChecklistQuestion) => {
    setEditingQ(q.id);
    setEditIndustries(q.industries ?? []);
    setEditWorkforceTypes(q.workforce_types ?? []);
    setEditWorkModes(q.work_modes ?? []);
    setEditOrgSizeBands(q.org_size_bands ?? []);
  };

  const saveIndustryEdit = async (q: ChecklistQuestion) => {
    await checklistAPI.updateQuestion(q.id, {
      industries: editIndustries.length > 0 ? editIndustries : null,
      workforce_types: editWorkforceTypes.length > 0 ? editWorkforceTypes : null,
      work_modes: editWorkModes.length > 0 ? editWorkModes : null,
      org_size_bands: editOrgSizeBands.length > 0 ? editOrgSizeBands : null,
    });
    setEditingQ(null);
    if (selectedCat) await loadQuestions(selectedCat);
  };

  const handleToggleActive = async (q: ChecklistQuestion) => {
    await checklistAPI.updateQuestion(q.id, { is_active: !q.is_active });
    if (selectedCat) await loadQuestions(selectedCat);
  };

  const handleDelete = async (q: ChecklistQuestion) => {
    if (!window.confirm('Deactivate this question?')) return;
    await checklistAPI.deleteQuestion(q.id);
    if (selectedCat) await loadQuestions(selectedCat);
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-10 w-10 border-b-2 border-blue-600 rounded-full" /></div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Checklist Administration</h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Categories sidebar */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-1">
          <h2 className="text-xs font-semibold text-gray-500 uppercase mb-3">Categories</h2>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => loadQuestions(cat)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                selectedCat?.id === cat.id ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <div>{cat.name}</div>
              <div className={`text-xs ${selectedCat?.id === cat.id ? 'text-blue-200' : 'text-gray-400'}`}>
                Weight: {cat.weight}%
              </div>
            </button>
          ))}
        </div>

        {/* Questions panel */}
        <div className="md:col-span-3 space-y-4">
          {selectedCat && (
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800">{selectedCat.name} — Questions ({questions.length})</h2>
              <button
                onClick={() => setShowNewQ(true)}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700"
              >
                + Add Question
              </button>
            </div>
          )}

          {/* New question form */}
          {showNewQ && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-semibold text-blue-800">New Question</h3>
              <textarea
                value={newQ.question_text}
                onChange={(e) => setNewQ({ ...newQ, question_text: e.target.value })}
                placeholder="Question text…"
                className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm resize-none"
                rows={2}
              />
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-500">Requirement Type</label>
                  <select value={newQ.requirement_type} onChange={(e) => setNewQ({ ...newQ, requirement_type: e.target.value })}
                    className="w-full mt-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs">
                    <option value="must_have">Must Have</option>
                    <option value="good_to_have">Good to Have</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Question Type</label>
                  <select value={newQ.question_type} onChange={(e) => setNewQ({ ...newQ, question_type: e.target.value })}
                    className="w-full mt-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs">
                    <option value="yes_no">Yes / No</option>
                    <option value="scale">Scale</option>
                    <option value="text">Text</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Max Score</label>
                  <input type="number" value={newQ.max_score} min={0.5} step={0.5}
                    onChange={(e) => setNewQ({ ...newQ, max_score: parseFloat(e.target.value) })}
                    className="w-full mt-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs" />
                </div>
              </div>
              <input value={newQ.guidance_note} onChange={(e) => setNewQ({ ...newQ, guidance_note: e.target.value })}
                placeholder="Guidance note (optional)…"
                className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm" />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={newQ.evidence_required}
                  onChange={(e) => setNewQ({ ...newQ, evidence_required: e.target.checked })} />
                Evidence required
              </label>
              <TagPicker
                label="Applicable Industries"
                allTags={allIndustries}
                selected={newQ.industries}
                onChange={(next) => setNewQ({ ...newQ, industries: next })}
                colorActive="bg-indigo-600 text-white border-indigo-600"
                colorAll="bg-indigo-600 text-white"
              />
              <TagPicker
                label="Workforce Types"
                allTags={workforceTypeOpts}
                selected={newQ.workforce_types}
                onChange={(next) => setNewQ({ ...newQ, workforce_types: next })}
                colorActive="bg-purple-600 text-white border-purple-600"
                colorAll="bg-purple-600 text-white"
              />
              <TagPicker
                label="Work Modes"
                allTags={workModeOpts}
                selected={newQ.work_modes}
                onChange={(next) => setNewQ({ ...newQ, work_modes: next })}
                colorActive="bg-teal-600 text-white border-teal-600"
                colorAll="bg-teal-600 text-white"
              />
              <TagPicker
                label="Organisation Size Bands"
                allTags={orgSizeBandOpts}
                selected={newQ.org_size_bands}
                onChange={(next) => setNewQ({ ...newQ, org_size_bands: next })}
                colorActive="bg-orange-600 text-white border-orange-600"
                colorAll="bg-orange-600 text-white"
              />
              <div className="flex gap-2">
                <button onClick={() => setShowNewQ(false)} className="flex-1 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm">Cancel</button>
                <button onClick={handleCreateQuestion} disabled={saving} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-60">
                  {saving ? 'Saving…' : 'Create Question'}
                </button>
              </div>
            </div>
          )}

          {/* Question list */}
          <div className="space-y-2">
            {questions.map((q) => (
              <div key={q.id} className={`bg-white border rounded-xl p-4 ${!q.is_active ? 'opacity-50' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        q.requirement_type === 'must_have' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {q.requirement_type === 'must_have' ? 'Must Have' : 'Good to Have'}
                      </span>
                      <span className="text-xs text-gray-400 capitalize">{q.question_type.replace('_', '/')}</span>
                      <span className="text-xs text-gray-400">Max: {q.max_score}</span>
                      {q.evidence_required && <span className="text-xs text-amber-600">📎 Evidence req.</span>}
                    </div>
                    <p className="text-sm text-gray-800">{q.question_text}</p>
                    {q.guidance_note && <p className="text-xs text-gray-500 mt-1 italic">{q.guidance_note}</p>}

                    {/* Targeting dimensions edit */}
                    {editingQ === q.id ? (
                      <div className="mt-3 border-t pt-3 space-y-3">
                        <TagPicker
                          label="Applicable Industries"
                          allTags={allIndustries}
                          selected={editIndustries}
                          onChange={setEditIndustries}
                          colorActive="bg-indigo-600 text-white border-indigo-600"
                          colorAll="bg-indigo-600 text-white"
                        />
                        <TagPicker
                          label="Workforce Types"
                          allTags={workforceTypeOpts}
                          selected={editWorkforceTypes}
                          onChange={setEditWorkforceTypes}
                          colorActive="bg-purple-600 text-white border-purple-600"
                          colorAll="bg-purple-600 text-white"
                        />
                        <TagPicker
                          label="Work Modes"
                          allTags={workModeOpts}
                          selected={editWorkModes}
                          onChange={setEditWorkModes}
                          colorActive="bg-teal-600 text-white border-teal-600"
                          colorAll="bg-teal-600 text-white"
                        />
                        <TagPicker
                          label="Organisation Size Bands"
                          allTags={orgSizeBandOpts}
                          selected={editOrgSizeBands}
                          onChange={setEditOrgSizeBands}
                          colorActive="bg-orange-600 text-white border-orange-600"
                          colorAll="bg-orange-600 text-white"
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => setEditingQ(null)}
                            className="text-xs px-2 py-1 border border-gray-300 rounded text-gray-600"
                          >Cancel</button>
                          <button
                            onClick={() => saveIndustryEdit(q)}
                            className="text-xs px-2 py-1 bg-blue-600 text-white rounded"
                          >Save Targeting</button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 space-y-1">
                        <div className="flex flex-wrap gap-1">
                          {!q.industries || q.industries.length === 0 ? (
                            <span className="text-xs text-gray-400 italic">All industries</span>
                          ) : (
                            q.industries.map((ind) => (
                              <span key={ind} className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded-full">{ind}</span>
                            ))
                          )}
                        </div>
                        {q.workforce_types && q.workforce_types.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {q.workforce_types.map((wt) => (
                              <span key={wt} className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded-full">{wt}</span>
                            ))}
                          </div>
                        )}
                        {q.work_modes && q.work_modes.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {q.work_modes.map((wm) => (
                              <span key={wm} className="text-xs bg-teal-50 text-teal-700 border border-teal-200 px-1.5 py-0.5 rounded-full">{wm}</span>
                            ))}
                          </div>
                        )}
                        {q.org_size_bands && q.org_size_bands.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {q.org_size_bands.map((sz) => (
                              <span key={sz} className="text-xs bg-orange-50 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded-full">{sz}</span>
                            ))}
                          </div>
                        )}
                        <button
                          onClick={() => openIndustryEdit(q)}
                          className="text-xs text-indigo-500 hover:underline mt-1"
                        >✎ Edit Targeting</button>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => handleToggleActive(q)} className="text-xs text-blue-500 hover:underline">
                      {q.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => handleDelete(q)} className="text-xs text-red-500 hover:underline">Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {questions.length === 0 && selectedCat && (
            <p className="text-center text-gray-400 py-8">No questions in this category yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
